import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { camelCase, snakeCase, startCase } from 'lodash';
import { SEARCH_MAPPINGS, SEARCH_MASTERDATA } from '../queries.ts';
import { MAPPING_LOOKUP_TYPES } from '../constants.ts';

import Table, { TableScrollWrapper } from '../../core/components/Table.tsx';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { withIcon } from '../../core/components/Icon.tsx';
import classnames from '../../core/utils/classnames.tsx';
import {
    MasterDataLoadingSkeleton,
    RESULTS_PAGE_SIZE,
    getTextForValue,
    EXCLUDED_MASTERDATA_COLUMNS,
    isNonEmpty,
    ExtraDataCell,
} from './MasterDataBrowser.tsx';
import HighlightText from '../../products/list_of_services/components/HighlightText.tsx';
import usePaginatedQuery from '../hooks/usePaginatedQuery.ts';
import StringField from '../../core/components/Fields/StringField.tsx';
import { useDebounce } from '../../core/utils/hooks/useDebounce.ts';
import { titleCase } from '../../core/utils/string.ts';

const LoadingIcon = withIcon(faSpinnerThird);

const MasterDataExplorerRow = ({
    entry,
    columns,
    columnSearchTerms,
    ...props
}: {
    entry: any;
    columns: string[];
    columnSearchTerms: Record<string, string>;
} & React.ComponentPropsWithRef<typeof Table.Row>) => {
    return (
        <Table.Row {...props}>
            {columns.map((key) => {
                if (key === 'extra_data') {
                    return <ExtraDataCell key={key} value={entry[camelCase(key)]} />;
                }
                const keywords = columnSearchTerms[key] ? [columnSearchTerms[key]] : [];
                return (
                    <Table.Cell key={key}>
                        <HighlightText text={getTextForValue(entry[camelCase(key)])} keywords={keywords} />
                    </Table.Cell>
                );
            })}
        </Table.Row>
    );
};

// lodash snakeCase adds underscore before digits (e.g. articleId1 → article_id_1),
// but the backend uses article_id1 format (no underscore before digit).
const toSnakeCase = (key: string) => snakeCase(key).replace(/_(\d)/g, '$1');

export const MasterDataExplorer = ({ lookupType, lookupDefinitionId, className }: any) => {
    const { t } = useTranslation('masterdata');

    const [columnSearchTerms, setColumnSearchTerms] = useState<Record<string, string>>({});
    const [debouncedColumnSearchTerms, setDebouncedColumnSearchTerms] = useState<Record<string, string>>({});
    const setDebouncedColumnSearchTermsDebounced = useDebounce(setDebouncedColumnSearchTerms, 300);

    const handleColumnSearchTermChange = (columnKey: string, value: string) => {
        const newSearchTerms = {
            ...columnSearchTerms,
            [columnKey]: value,
        };
        setColumnSearchTerms(newSearchTerms);
        setDebouncedColumnSearchTermsDebounced(newSearchTerms);
    };

    const searchQuery = MAPPING_LOOKUP_TYPES.includes(lookupType) ? SEARCH_MAPPINGS : SEARCH_MASTERDATA;
    const { pages, loading, error, hasNextPage, loadNextPage, nextPageLoading } = usePaginatedQuery(searchQuery, {
        initialPageVariables: {
            from: 0,
            size: RESULTS_PAGE_SIZE,
        },
        getNextPageVariables: (lastPage, lastPageVariables) => {
            const newEdges = lastPage?.assistanceSearch?.edges || [];
            if (newEdges.length < RESULTS_PAGE_SIZE) {
                return null;
            }
            return {
                ...lastPageVariables,
                from: lastPageVariables.from + RESULTS_PAGE_SIZE,
            };
        },
        variables: {
            lookupDefinitionId,
            searchFields: Object.entries(debouncedColumnSearchTerms)
                .filter(([name, value]) => Boolean(value))
                .map(([name, value]) => ({ name, value })),
        },
        fetchPolicy: 'cache-and-network',
        skip: !lookupDefinitionId,
    });

    const browsableKey = `${titleCase(lookupType)}Type`;

    const [rows, setRows] = useState<any[]>([]);
    useEffect(() => {
        // keep stale data until new data is ready
        if (pages.length === 0) return;
        const nextRows = pages.flatMap((page) => (page?.assistanceSearch?.edges || []).map((edge) => edge.node));
        setRows(nextRows);
    }, [pages]);

    const [searchableFields, setSearchableFields] = useState<string[]>([]);
    useEffect(() => {
        if (pages.length > 0 && searchableFields.length === 0) {
            const fields = pages[0]?.assistanceSearch?.searchableFields || [];
            setSearchableFields(fields);
        }
    }, [pages]);

    const [columns, setColumns] = useState<string[]>([]);
    useEffect(() => {
        // keep stale columns when rows become empty (e.g. search returns no results)
        if (rows.length === 0) return;
        const baseColumns = Object.keys(rows[0])
            .filter((key) => !EXCLUDED_MASTERDATA_COLUMNS.has(key) && key !== 'extraData')
            .filter((key) => rows.some((row) => isNonEmpty(row, key)))
            .map(toSnakeCase);
        const hasExtraData = rows.some((row) => isNonEmpty(row, 'extraData'));
        setColumns(hasExtraData ? [...baseColumns, 'extra_data'] : baseColumns);
    }, [rows]);

    const [isInitialLoad, setIsInitialLoad] = useState(true);
    useEffect(() => {
        if (!loading) {
            setIsInitialLoad(false);
        }
    }, [loading]);

    const displayColumns = columns.length > 0 ? columns : searchableFields;

    return (
        <div
            className={classnames(
                'flex flex-col gap-6 pb-6 overflow-hidden relative',
                // full-screen height by default; embedded usages pass their own
                className ?? '!h-[calc(100dvh-7rem)]'
            )}
        >
            {isInitialLoad && rows.length === 0 ? (
                // mirrors the shape of the TableScrollWrapper the table renders in
                <div className="flex flex-col gap-3 flex-1 overflow-hidden rounded-lg border border-solid border-line-weak bg-surface-weak p-8">
                    <MasterDataLoadingSkeleton />
                    <MasterDataLoadingSkeleton />
                    <MasterDataLoadingSkeleton />
                </div>
            ) : (
                <TableScrollWrapper>
                    <Table className="w-full table-auto">
                        <Table.Head sticky>
                            <Table.Row>
                                {displayColumns.map((key) => (
                                    <Table.HeadCell
                                        key={key}
                                        className={classnames(key === 'description' ? 'min-w-[20rem]' : '')}
                                    >
                                        {t(`schema.${browsableKey}.${camelCase(key)}`, {
                                            defaultValue: startCase(camelCase(key)),
                                        })}
                                    </Table.HeadCell>
                                ))}
                            </Table.Row>

                            <Table.Row>
                                {displayColumns.map((key) => (
                                    <Table.Cell key={key} className="group-last:border-b !rounded-none">
                                        <StringField
                                            value={columnSearchTerms[key] || ''}
                                            onValueChange={(value: string) => handleColumnSearchTermChange(key, value)}
                                            placeholder={t('explorer.searchPlaceholder')}
                                            disabled={!searchableFields.includes(key)}
                                        />
                                    </Table.Cell>
                                ))}
                            </Table.Row>
                        </Table.Head>
                        <Table.Body className={classnames((loading || nextPageLoading) && 'opacity-50')}>
                            {rows.length === 0 && !loading ? (
                                <Table.Row>
                                    <Table.Cell
                                        colSpan={displayColumns.length || 1}
                                        className="text-center text-content-secondary py-8"
                                    >
                                        {error
                                            ? t('explorer.loadError', {
                                                  defaultValue: 'The data could not be loaded. Please try again later.',
                                              })
                                            : t('explorer.noResults', { defaultValue: 'No results found' })}
                                    </Table.Cell>
                                </Table.Row>
                            ) : (
                                rows.map((row, index) => (
                                    <MasterDataExplorerRow
                                        key={row.id || index}
                                        entry={row}
                                        columns={displayColumns}
                                        columnSearchTerms={columnSearchTerms}
                                    />
                                ))
                            )}

                            {hasNextPage && (
                                <Table.Row>
                                    <Table.Cell colSpan={displayColumns.length || 1} className="!p-0">
                                        <button
                                            type="button"
                                            onClick={loadNextPage}
                                            disabled={loading || nextPageLoading}
                                            className="flex w-full h-14 items-center justify-center gap-2 text-sm text-content-brand-strong cursor-pointer hover:bg-surface-strong transition-colors duration-200 disabled:opacity-50 disabled:cursor-default"
                                        >
                                            {(loading || nextPageLoading) && <LoadingIcon spin />}
                                            {loading || nextPageLoading
                                                ? t('explorer.loading')
                                                : t('explorer.loadMore', { defaultValue: 'Load more' })}
                                        </button>
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </TableScrollWrapper>
            )}
        </div>
    );
};
